import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  TimePicker,
  Card,
  Row,
  Col,
  Typography,
  message,
  Select,
  InputNumber,
  Checkbox,
  Input,
  Divider,
  Radio,
} from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useTranslation } from "react-i18next";
import { companyService } from "../../app/services";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Title } = Typography;
const { Option } = Select;

const PreferencesPage = ({ company }) => {
  const [form] = Form.useForm();
  const [services, setServices] = useState([]);
  const [customConfig, setCustomConfig] = useState({});
  const { t } = useTranslation();

  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const dayMap = {
    mon: "Monday",
    tue: "Tuesday",
    wed: "Wednesday",
    thu: "Thursday",
    fri: "Friday",
    sat: "Saturday",
    sun: "Sunday",
  };

  // ✅ Load data (services + working hours + custom config)
  useEffect(() => {
    const loadData = async () => {
      try {
        const svc = await companyService.getCompanyServicesByParameter(
          company,
          "",
          ""
        );
        setServices(svc);
        debugger;
        const companyData =
          await companyService.getCompaniesByCategoryAndLocationAndCompanyid(
            "",
            {},
            company
          );

        const values = {};
        days.forEach((day) => {
          values[day] = { open: null, close: null }; // default empty
        });

        if (companyData.length > 0) {
          // ✅ Load workingHours if exists
          if (companyData[0]?.workingHours) {
            // Example: "mon;07:00-17:00|tue;08:00-16:00|sat;"
            const parts = companyData[0].workingHours.split("|");

            parts.forEach((part) => {
              if (!part) return;
              const [dayKey, timeRange] = part.split(";");
              const fullDay = dayMap[dayKey];
              if (!fullDay) return;

              if (timeRange) {
                const [open, close] = timeRange.split("-");
                values[fullDay] = {
                  open: open ? dayjs.utc(open, "HH:mm").local() : null,
                  close: close ? dayjs.utc(close, "HH:mm").local() : null,
                };
              }
            });
          } else {
            // Default 09:00-18:00
            days.forEach((day) => {
              values[day] = {
                open: dayjs("09:00", "HH:mm"),
                close: dayjs("18:00", "HH:mm"),
              };
            });
          }

          // ✅ Load custom config from Parameters (stringified JSON)
          if (companyData[0]?.parameters) {
            try {
              
              const parsed = JSON.parse(companyData[0].parameters);
              setCustomConfig(parsed);
              values.customConfig = parsed; // prefill form
            } catch (e) {
              console.warn("Invalid customConfig JSON", e);
            }
          }
        }

        form.setFieldsValue(values);
      } catch (err) {
        console.error("Failed to fetch company data:", err);
      }
    };
    loadData();
  }, [company]);


  const buildUpdatedConfig = (currentConfig, formValues) => {
  const updated = { ...currentConfig };
  Object.keys(currentConfig).forEach((key) => {
    if (formValues.customConfig && formValues.customConfig[key] !== undefined) {
      updated[key] = {
        ...currentConfig[key], // keep type + options
        value: formValues.customConfig[key], // update only value
      };
    }
  });
  return updated;
};
const normalizeConfig = (config) => {
  const normalized = {};
  Object.keys(config).forEach((key) => {
    const item = config[key];

    // If item.value itself is an object with { value: X }, unwrap it
    let value = item.value;
    if (value && typeof value === "object" && "value" in value) {
      value = value.value;
    }

    normalized[key] = {
      ...item,
      value, // overwrite with normalized value
    };
  });
  return normalized;
};
  // ✅ Save preferences
  const onFinish = async (values) => {
    try {
      const reverseDayMap = Object.fromEntries(
        Object.entries(dayMap).map(([k, v]) => [v, k])
      );
      const parts = [];

      days.forEach((day) => {
        if (values[day]?.open && values[day]?.close) {
          const open = values[day].open.utc().format("HH:mm");
          const close = values[day].close.utc().format("HH:mm");
          parts.push(`${reverseDayMap[day]};${open}-${close}`);
        } else {
          parts.push(`${reverseDayMap[day]};`); // closed
        }
      });

      const workingHours = parts.join("|");

      // ✅ Extract custom config from form
      
    const normalizedCustomConfig = buildUpdatedConfig(customConfig, values);

    const updatedCustomConfig = normalizeConfig(normalizedCustomConfig);
      // Save working hours
      await companyService.saveWorkingHours(company, workingHours);

      // Save custom config as JSON string
        await companyService.saveCustomConfigCompany(company, JSON.stringify(updatedCustomConfig));

      message.success(t("PreferencesPage.save_success"));
    } catch (error) {
      console.error("Error saving preferences:", error);
      message.error(t("PreferencesPage.save_fail"));
    }
  };

  // ✅ Dynamic Config Renderer
  const renderDynamicControls = (jsonConfig) => {
    return Object.entries(jsonConfig).map(([key, config]) => {
      const label = key.charAt(0).toUpperCase() + key.slice(1);

      switch (config.type) {
        case "checkbox":
          return (
            <Form.Item
              key={key}
              name={["customConfig", key, "value"]}
              valuePropName="checked"
              label={label}
            >
              <Checkbox defaultChecked={config.value}>{label}</Checkbox>
            </Form.Item>
          );
        case "number":
          return (
            <Form.Item
              key={key}
              name={["customConfig", key, "value"]}
              label={label}
            >
              <InputNumber
                min={1}
                defaultValue={config.value}
                style={{ width: "100%" }}
              />
            </Form.Item>
          );
        case "text":
          return (
            <Form.Item
              key={key}
              name={["customConfig", key, "value"]}
              label={label}
            >
              <Input defaultValue={config.value} />
            </Form.Item>
          );
        case "dropdown":
          return (
            <Form.Item
              key={key}
              name={["customConfig", key, "value"]}
              label={label}
            >
              <Select defaultValue={config.value}>
                {config.options.map((opt) => (
                  <Select.Option key={opt} value={opt}>
                    {opt}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          );
        case "radio":
          return (
            <Form.Item
              key={key}
              name={["customConfig", key, "value"]}
              label={label}
            >
              <Radio.Group defaultValue={config.value}>
                {config.options.map((opt) => (
                  <Radio key={opt} value={opt}>
                    {opt}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
          );
        default:
          return null;
      }
    });
  };

  return (
    <div style={{ padding: "24px" }}>
      <Card
        style={{
          maxWidth: 800,
          margin: "0 auto",
          borderRadius: 12,
          boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
          padding: "32px",
        }}
      >
        <Title level={3} style={{ marginBottom: 24 }}>
          {t("PreferencesPage.title")}
        </Title>

        <Form layout="vertical" form={form} onFinish={onFinish}>
          {/* Opening Hours */}
          <Title level={4}>{t("PreferencesPage.opening_hours")}</Title>
          {days.map((day) => (
            <Row gutter={16} key={day} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <strong>{t(`PreferencesPage.days.${day.toLowerCase()}`)}</strong>
              </Col>
              <Col span={8}>
                <Form.Item name={[day, "open"]} noStyle>
                  <TimePicker style={{ width: "100%" }} format="HH:mm" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={[day, "close"]} noStyle>
                  <TimePicker style={{ width: "100%" }} format="HH:mm" />
                </Form.Item>
              </Col>
            </Row>
          ))}

          <Divider />

          {/* Custom Config */}
          <Title level={4}>{t("PreferencesPage.custom_config")}</Title>
          {renderDynamicControls(customConfig)}

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              {t("PreferencesPage.save")}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default PreferencesPage;


// import React, { useState, useEffect } from "react";
// import {
//   Form,
//   Button,
//   TimePicker,
//   Card,
//   Row,
//   Col,
//   Typography,
//   message,
//   Select,
//   InputNumber,
//   Checkbox,
//   Input,
//   Divider,
//   Radio,
// } from "antd";
// import dayjs from "dayjs";
// import utc from "dayjs/plugin/utc";
// import timezone from "dayjs/plugin/timezone";
// import { useTranslation } from "react-i18next";
// import { companyService } from "../../app/services";

// dayjs.extend(utc);
// dayjs.extend(timezone);

// const { Title } = Typography;
// const { Option } = Select;

// const PreferencesPage = ({ company }) => {
//   const [form] = Form.useForm();
//   const [services, setServices] = useState([]);
//   const [customConfig, setCustomConfig] = useState({
//     capacity: { type: "number", value: 2 },
//     payment: { type: "checkbox", value: true },
//     notes: { type: "text", value: "Optional comment" },
//     bookingType: {
//       type: "dropdown",
//       value: "online",
//       options: ["online", "in-person", "phone"],
//     },
//     reminder: {
//       type: "radio",
//       value: "email",
//       options: ["email", "sms", "none"],
//     },
//   });

//   const { t } = useTranslation();

//   const days = [
//     "Monday",
//     "Tuesday",
//     "Wednesday",
//     "Thursday",
//     "Friday",
//     "Saturday",
//     "Sunday",
//   ];

//   const dayMap = {
//     mon: "Monday",
//     tue: "Tuesday",
//     wed: "Wednesday",
//     thu: "Thursday",
//     fri: "Friday",
//     sat: "Saturday",
//     sun: "Sunday",
//   };

//   // ✅ Load data (services + working hours)
//   useEffect(() => {
//     const loadData = async () => {
//       try {
//         const svc = await companyService.getCompanyServicesByParameter(company, "", "");
//         setServices(svc);

//         const companyData = await companyService.getCompaniesByCategoryAndLocationAndCompanyid("", {}, company);

//         const values = {};
//         days.forEach((day) => {
//           values[day] = { open: null, close: null }; // default empty
//         });

//         if (companyData.length > 0 && companyData[0]?.workingHours) {
//           // Example: "mon;07:00-17:00|tue;08:00-16:00|sat;"
//           const parts = companyData[0].workingHours.split("|");

//           parts.forEach((part) => {
//             if (!part) return;
//             const [dayKey, timeRange] = part.split(";");
//             const fullDay = dayMap[dayKey];
//             if (!fullDay) return;

//             if (timeRange) {
//               const [open, close] = timeRange.split("-");
//               values[fullDay] = {
//                 open: open ? dayjs.utc(open, "HH:mm").local() : null,
//                 close: close ? dayjs.utc(close, "HH:mm").local() : null,
//               };
//             }
//           });
//         } else {
//           // Default 09:00-18:00
//           days.forEach((day) => {
//             values[day] = {
//               open: dayjs("09:00", "HH:mm"),
//               close: dayjs("18:00", "HH:mm"),
//             };
//           });
//         }

//         form.setFieldsValue(values);
//       } catch (err) {
//         console.error("Failed to fetch company data:", err);
//       }
//     };
//     loadData();
//   }, [company]);

//   // ✅ Save preferences
//   const onFinish = async (values) => {
//     try {
//       const reverseDayMap = Object.fromEntries(Object.entries(dayMap).map(([k, v]) => [v, k]));
//       const parts = [];

//       days.forEach((day) => {
//         if (values[day]?.open && values[day]?.close) {
//           const open = values[day].open.utc().format("HH:mm");
//           const close = values[day].close.utc().format("HH:mm");
//           parts.push(`${reverseDayMap[day]};${open}-${close}`);
//         } else {
//           // if closed, still include day with no hours
//           parts.push(`${reverseDayMap[day]};`);
//         }
//       });

//       const workingHours = parts.join("|");

//       await companyService.saveWorkingHours(company, workingHours);

//       const payload = { ...values, customConfig };
//       // await companyService.updateCompanyPreferences(company, payload);

//       message.success(t("PreferencesPage.save_success"));
//     } catch (error) {
//       console.error("Error saving preferences:", error);
//       message.error(t("PreferencesPage.save_fail"));
//     }
//   };

//   // ✅ Dynamic Config
//   const renderDynamicControls = (jsonConfig) => {
//     return Object.entries(jsonConfig).map(([key, config]) => {
//       const label = key.charAt(0).toUpperCase() + key.slice(1);

//       switch (config.type) {
//         case "checkbox":
//           return (
//             <Form.Item key={key} name={["customConfig", key]} valuePropName="checked" label={label}>
//               <Checkbox defaultChecked={config.value}>{label}</Checkbox>
//             </Form.Item>
//           );
//         case "number":
//           return (
//             <Form.Item key={key} name={["customConfig", key]} label={label}>
//               <InputNumber min={1} defaultValue={config.value} style={{ width: "100%" }} />
//             </Form.Item>
//           );
//         case "text":
//           return (
//             <Form.Item key={key} name={["customConfig", key]} label={label}>
//               <Input defaultValue={config.value} />
//             </Form.Item>
//           );
//         case "dropdown":
//           return (
//             <Form.Item key={key} name={["customConfig", key]} label={label}>
//               <Select defaultValue={config.value}>
//                 {config.options.map((opt) => (
//                   <Select.Option key={opt} value={opt}>
//                     {opt}
//                   </Select.Option>
//                 ))}
//               </Select>
//             </Form.Item>
//           );
//         case "radio":
//           return (
//             <Form.Item key={key} name={["customConfig", key]} label={label}>
//               <Radio.Group defaultValue={config.value}>
//                 {config.options.map((opt) => (
//                   <Radio key={opt} value={opt}>
//                     {opt}
//                   </Radio>
//                 ))}
//               </Radio.Group>
//             </Form.Item>
//           );
//         default:
//           return null;
//       }
//     });
//   };

//   return (
//     <div style={{ padding: "24px" }}>
//       <Card
//         style={{
//           maxWidth: 800,
//           margin: "0 auto",
//           borderRadius: 12,
//           boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
//           padding: "32px",
//         }}
//       >
//         <Title level={3} style={{ marginBottom: 24 }}>
//           {t("PreferencesPage.title")}
//         </Title>

//         <Form layout="vertical" form={form} onFinish={onFinish}>
//           {/* Opening Hours */}
//           <Title level={4}>{t("PreferencesPage.opening_hours")}</Title>
//           {days.map((day) => (
//             <Row gutter={16} key={day} style={{ marginBottom: 16 }}>
//               <Col span={8}>
//                 <strong>{t(`PreferencesPage.days.${day.toLowerCase()}`)}</strong>
//               </Col>
//               <Col span={8}>
//                 <Form.Item name={[day, "open"]} noStyle>
//                   <TimePicker style={{ width: "100%" }} format="HH:mm" />
//                 </Form.Item>
//               </Col>
//               <Col span={8}>
//                 <Form.Item name={[day, "close"]} noStyle>
//                   <TimePicker style={{ width: "100%" }} format="HH:mm" />
//                 </Form.Item>
//               </Col>
//             </Row>
//           ))}

//           {/* <Divider />

//           <Title level={4}>{t("PreferencesPage.available_services")}</Title>
//           <Form.Item
//             name="services"
//             rules={[{ required: true, message: t("PreferencesPage.services_required") }]}
//           >
//             <Select
//               mode="multiple"
//               placeholder={t("PreferencesPage.services_placeholder")}
//               optionLabelProp="label"
//             >
//               {services.map((service) => (
//                 <Option key={service.id} value={service.id} label={t(service.name)}>
//                   <span
//                     dangerouslySetInnerHTML={{ __html: service.icon }}
//                     style={{ width: 20, height: 20, display: "inline-block" }}
//                   />
//                   &nbsp;{t(service.name)} ({service.Price} CHF)
//                 </Option>
//               ))}
//             </Select>
//           </Form.Item> */}

//           <Divider />

//           {/* Custom Config */}
//           <Title level={4}>{t("PreferencesPage.custom_config")}</Title>
//           {renderDynamicControls(customConfig)}

//           <Form.Item>
//             <Button type="primary" htmlType="submit" block>
//               {t("PreferencesPage.save")}
//             </Button>
//           </Form.Item>
//         </Form>
//       </Card>
//     </div>
//   );
// };

// export default PreferencesPage;
